import React, { useState, useEffect } from "react";
import {
  getRateLimitHealth,
  RateLimitHealth,
  BatchTimingMetric,
  RateLimitConfig,
  updateRateLimitConfig,
  resetRateLimitConfig,
  SharePointRateLimitHealth,
  SharePointRateLimitConfig,
  getSharePointRateLimitConfig,
  updateSharePointRateLimitConfig,
  resetSharePointRateLimitConfig,
} from "../lib/api";
import { Progress } from "./ui/progress";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Alert, AlertTitle, AlertDescription } from "./ui/alert";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { ReloadIcon } from "@radix-ui/react-icons";
import { formatDistanceToNow } from "date-fns";

interface RateLimitData {
  health: RateLimitHealth;
  summary: string;
  recentBatches: BatchTimingMetric[];
}

export function RateLimitMonitor() {
  const [data, setData] = useState<RateLimitData | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshInterval, setRefreshInterval] = useState<number | null>(null);

  const fetchStatus = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getRateLimitHealth();
      if (response.success && response.health) {
        setData({
          health: response.health,
          summary: response.summary || '',
          recentBatches: response.recentBatches || [],
        });
        setLastUpdated(new Date());
      } else {
        setError(response.error || "Failed to fetch rate limit data");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    return () => {
      if (refreshInterval !== null) {
        clearInterval(refreshInterval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleAutoRefresh = () => {
    if (refreshInterval !== null) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    } else {
      const interval = window.setInterval(() => {
        fetchStatus();
      }, 5000); // Refresh every 5 seconds
      setRefreshInterval(interval);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'healthy': return 'default';
      case 'elevated': return 'secondary';
      case 'critical': return 'destructive';
      default: return 'outline';
    }
  };

  const getLoadColor = (percent: number) => {
    if (percent < 50) return "bg-green-500";
    if (percent < 80) return "bg-yellow-500";
    return "bg-red-500";
  };

  const getSlotColor = (percent: number) => {
    if (percent > 70) return "bg-green-500";
    if (percent > 30) return "bg-yellow-500";
    return "bg-red-500";
  };

  return (
    <Card className="w-full h-fit">
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="text-lg font-bold">Dataverse API Concurrency</CardTitle>
            <CardDescription>Batch concurrency control with performance tracking</CardDescription>
          </div>
          <div className="flex gap-2">
            {data?.health && (
              <Badge variant={getStatusBadgeVariant(data.health.status)}>
                {data.health.status.toUpperCase()}
              </Badge>
            )}
            <Badge variant={refreshInterval !== null ? "default" : "outline"}>
              {refreshInterval !== null ? "Live" : "Paused"}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {data?.health ? (
          <>
            {/* Load Percent */}
            <div className="space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span>API Load</span>
                <span className="font-mono">{data.health.loadPercent}%</span>
              </div>
              <Progress
                value={data.health.loadPercent}
                className={getLoadColor(data.health.loadPercent)}
                max={100}
              />
            </div>

            {/* Available Batch Slots */}
            <div className="space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span>Available Batch Slots</span>
                <span className="font-mono">
                  {data.health.availableSlots ?? data.health.availableTokens} / {data.health.maxConcurrentBatches ?? data.health.maxTokens}
                </span>
              </div>
              <Progress
                value={((data.health.availableSlots ?? data.health.availableTokens) / (data.health.maxConcurrentBatches ?? data.health.maxTokens)) * 100}
                className={getSlotColor(((data.health.availableSlots ?? data.health.availableTokens) / (data.health.maxConcurrentBatches ?? data.health.maxTokens)) * 100)}
                max={100}
              />
            </div>

            {/* Key Metrics Grid */}
            <div className="grid grid-cols-2 gap-4 text-sm pt-2">
              <div className="p-2 bg-gray-50 rounded">
                <div className="text-gray-500 text-xs">Batch Throughput</div>
                <div className="font-semibold">{data.health.batchesPerSecond ?? data.health.currentRate} / {data.health.maxBatchesPerSecond ?? data.health.maxRate} batches/s</div>
              </div>
              <div className="p-2 bg-gray-50 rounded">
                <div className="text-gray-500 text-xs">Avg Response Time</div>
                <div className="font-semibold">{data.health.avgMsPerRecord} ms/record</div>
              </div>
              <div className="p-2 bg-gray-50 rounded">
                <div className="text-gray-500 text-xs">Record Throughput</div>
                <div className="font-semibold">
                  {data.health.avgRecordsPerSecond?.toFixed(1) ?? '-'} records/s
                </div>
              </div>
              <div className="p-2 bg-gray-50 rounded">
                <div className="text-gray-500 text-xs">Batch Size</div>
                <div className="font-semibold">
                  {data.health.batchSize} records
                  <span className="text-xs text-gray-500 ml-2">
                    ({data.health.slotCostPerBatch ?? data.health.tokenCostPerBatch} slot/batch)
                  </span>
                </div>
              </div>
            </div>

            {/* Config Source indicator */}
            <div className="text-xs text-gray-500 pt-2 border-t">
              Config source: <Badge variant="outline" className="ml-1 text-xs">
                {data.health.configSource?.toUpperCase() || 'DEFAULT'}
              </Badge>
            </div>
          </>
        ) : (
          <div className="h-20 flex items-center justify-center">
            {loading ? (
              <ReloadIcon className="h-6 w-6 animate-spin" />
            ) : (
              <span className="text-gray-500">No data available</span>
            )}
          </div>
        )}
      </CardContent>

      <CardFooter className="flex justify-between pt-2 border-t">
        <div className="text-xs text-gray-500">
          {lastUpdated
            ? `Updated ${formatDistanceToNow(lastUpdated)} ago`
            : "Never updated"}
        </div>
        <div className="flex space-x-2">
          <Button
            onClick={toggleAutoRefresh}
            variant="outline"
            size="sm"
          >
            {refreshInterval !== null ? "Stop" : "Auto-refresh"}
          </Button>
          <Button
            onClick={fetchStatus}
            size="sm"
            disabled={loading}
          >
            {loading ? (
              <ReloadIcon className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <ReloadIcon className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}

// New component for recent batch timing
export function RecentBatchTimings() {
  const [batches, setBatches] = useState<BatchTimingMetric[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBatches = async () => {
    setLoading(true);
    try {
      const response = await getRateLimitHealth();
      if (response.success && response.recentBatches) {
        setBatches(response.recentBatches);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch batches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBatches();
    const interval = setInterval(fetchBatches, 10000);
    return () => clearInterval(interval);
  }, []);

  if (error) {
    return (
      <Alert variant="destructive">
        <AlertTitle>Error</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (loading && batches.length === 0) {
    return (
      <div className="flex items-center justify-center h-32">
        <ReloadIcon className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (batches.length === 0) {
    return (
      <div className="text-center text-gray-500 py-8">
        No recent batch data. Start a SQL Sync to see timing metrics.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-6 gap-2 text-xs font-semibold text-gray-500 px-2">
        <div>Time</div>
        <div>Entity</div>
        <div className="text-right">Records</div>
        <div className="text-right">Duration</div>
        <div className="text-right">ms/rec</div>
        <div className="text-right">Slots</div>
      </div>
      {batches.slice().reverse().map((batch, idx) => (
        <div
          key={idx}
          className="grid grid-cols-6 gap-2 text-sm px-2 py-1 bg-gray-50 rounded hover:bg-gray-100"
        >
          <div className="text-gray-500">
            {new Date(batch.timestamp).toLocaleTimeString()}
          </div>
          <div className="font-medium truncate">{batch.entityType}</div>
          <div className="text-right font-mono">{batch.recordCount}</div>
          <div className="text-right font-mono">{batch.dataverseDurationMs}ms</div>
          <div className={`text-right font-mono ${batch.msPerRecord > 30 ? 'text-red-600' : batch.msPerRecord > 20 ? 'text-yellow-600' : 'text-green-600'}`}>
            {Math.round(batch.msPerRecord)}
          </div>
          <div className="text-right font-mono">{batch.tokenCostUsed}</div>
        </div>
      ))}
    </div>
  );
}

// Rate Limit Configuration Editor
interface RateLimitConfigEditorProps {
  currentConfig?: RateLimitConfig;
  onConfigUpdated?: () => void;
}

export function RateLimitConfigEditor({ currentConfig, onConfigUpdated }: RateLimitConfigEditorProps) {
  const [rateLimit, setRateLimit] = useState<number>(currentConfig?.rateLimitPerSecond || 5);
  const [burstLimit, setBurstLimit] = useState<number>(currentConfig?.burstLimit || 40);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Update local state when config changes
  useEffect(() => {
    if (currentConfig) {
      setRateLimit(currentConfig.rateLimitPerSecond);
      setBurstLimit(currentConfig.burstLimit);
    }
  }, [currentConfig]);

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    setSaving(true);

    try {
      // Validate - Dataverse allows ~20 req/sec sustained, 52 concurrent
      if (rateLimit < 1 || rateLimit > 52) {
        throw new Error('Batch throughput must be between 1 and 52');
      }
      if (burstLimit < 1 || burstLimit > 52) {
        throw new Error('Max concurrent batches must be between 1 and 52 (Dataverse limit)');
      }
      if (burstLimit < rateLimit) {
        throw new Error('Max concurrent batches must be >= batch throughput');
      }

      const response = await updateRateLimitConfig(rateLimit, burstLimit);
      if (response.success) {
        setSuccess(`Concurrency settings updated: ${rateLimit} batches/sec, max ${burstLimit} concurrent`);
        onConfigUpdated?.();
      } else {
        throw new Error(response.error || 'Failed to update config');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setError(null);
    setSuccess(null);
    setResetting(true);

    try {
      const response = await resetRateLimitConfig();
      if (response.success) {
        setSuccess('Concurrency settings reset to environment defaults');
        if (response.config) {
          setRateLimit(response.config.rateLimitPerSecond);
          setBurstLimit(response.config.burstLimit);
        }
        onConfigUpdated?.();
      } else {
        throw new Error(response.error || 'Failed to reset config');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setResetting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Concurrency Settings</CardTitle>
        <CardDescription>
          Adjust batch concurrency settings. Changes are stored in Redis and apply immediately.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert>
            <AlertTitle>Success</AlertTitle>
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="rateLimit">Batch Throughput (batches/sec)</Label>
            <Input
              id="rateLimit"
              type="number"
              min={1}
              max={52}
              value={rateLimit}
              onChange={(e) => setRateLimit(parseInt(e.target.value) || 1)}
            />
            <p className="text-xs text-gray-500">
              How fast new batches can start (default: 5, max: 20)
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="burstLimit">Max Concurrent Batches</Label>
            <Input
              id="burstLimit"
              type="number"
              min={1}
              max={52}
              value={burstLimit}
              onChange={(e) => setBurstLimit(parseInt(e.target.value) || 1)}
            />
            <p className="text-xs text-gray-500">
              Maximum batches running simultaneously (default: 40, max: 52)
            </p>
          </div>
        </div>

        {/* Quick presets */}
        <div className="pt-2">
          <p className="text-xs text-gray-500 mb-2">Quick presets:</p>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setRateLimit(3); setBurstLimit(20); }}
            >
              Low (3/sec, max 20)
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setRateLimit(5); setBurstLimit(40); }}
            >
              Normal (5/sec, max 40)
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setRateLimit(10); setBurstLimit(52); }}
            >
              High (10/sec, max 52)
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setRateLimit(20); setBurstLimit(52); }}
            >
              Maximum (20/sec, max 52)
            </Button>
          </div>
        </div>

        {currentConfig && (
          <div className="text-xs text-gray-500 pt-2 border-t">
            Current config source: <Badge variant="outline" className="ml-1">
              {currentConfig.source.toUpperCase()}
            </Badge>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button
          variant="outline"
          onClick={handleReset}
          disabled={resetting || saving}
        >
          {resetting ? (
            <ReloadIcon className="h-4 w-4 animate-spin mr-2" />
          ) : null}
          Reset to Defaults
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || resetting}
        >
          {saving ? (
            <ReloadIcon className="h-4 w-4 animate-spin mr-2" />
          ) : null}
          Save Changes
        </Button>
      </CardFooter>
    </Card>
  );
}

// SharePoint Rate Limit Monitor
export function SharePointRateLimitMonitor() {
  const [health, setHealth] = useState<SharePointRateLimitHealth | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshInterval, setRefreshInterval] = useState<number | null>(null);

  const fetchStatus = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getRateLimitHealth();
      if (response.success && response.sharePointHealth) {
        setHealth(response.sharePointHealth);
        setLastUpdated(new Date());
      } else {
        setError(response.error || "Failed to fetch SharePoint rate limit data");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    return () => {
      if (refreshInterval !== null) {
        clearInterval(refreshInterval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleAutoRefresh = () => {
    if (refreshInterval !== null) {
      clearInterval(refreshInterval);
      setRefreshInterval(null);
    } else {
      const interval = window.setInterval(() => {
        fetchStatus();
      }, 5000);
      setRefreshInterval(interval);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case 'healthy': return 'default';
      case 'elevated': return 'secondary';
      case 'critical': return 'destructive';
      default: return 'outline';
    }
  };

  const getLoadColor = (percent: number) => {
    if (percent < 50) return "bg-green-500";
    if (percent < 80) return "bg-yellow-500";
    return "bg-red-500";
  };

  const getSlotColor = (percent: number) => {
    if (percent > 70) return "bg-green-500";
    if (percent > 30) return "bg-yellow-500";
    return "bg-red-500";
  };

  return (
    <Card className="w-full h-fit">
      <CardHeader>
        <div className="flex justify-between items-center">
          <div>
            <CardTitle className="text-lg font-bold">SharePoint/Graph API</CardTitle>
            <CardDescription>Rate limiting for Microsoft Graph API requests</CardDescription>
          </div>
          <div className="flex gap-2">
            {health && (
              <Badge variant={getStatusBadgeVariant(health.status)}>
                {health.status.toUpperCase()}
              </Badge>
            )}
            <Badge variant={refreshInterval !== null ? "default" : "outline"}>
              {refreshInterval !== null ? "Live" : "Paused"}
            </Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {health ? (
          <>
            {/* Load Percent */}
            <div className="space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span>API Load</span>
                <span className="font-mono">{health.loadPercent}%</span>
              </div>
              <Progress
                value={health.loadPercent}
                className={getLoadColor(health.loadPercent)}
                max={100}
              />
            </div>

            {/* Available Tokens */}
            <div className="space-y-2">
              <div className="flex justify-between items-center text-sm">
                <span>Available Tokens</span>
                <span className="font-mono">
                  {health.availableTokens} / {health.maxTokens}
                </span>
              </div>
              <Progress
                value={(health.availableTokens / health.maxTokens) * 100}
                className={getSlotColor((health.availableTokens / health.maxTokens) * 100)}
                max={100}
              />
            </div>

            {/* Key Metrics Grid */}
            <div className="grid grid-cols-2 gap-4 text-sm pt-2">
              <div className="p-2 bg-gray-50 rounded">
                <div className="text-gray-500 text-xs">Current Rate</div>
                <div className="font-semibold">{health.currentRate} / {health.maxRate} req/s</div>
              </div>
              <div className="p-2 bg-gray-50 rounded">
                <div className="text-gray-500 text-xs">Max Concurrent</div>
                <div className="font-semibold">{health.maxTokens} requests</div>
              </div>
            </div>

            {/* Config Source indicator */}
            <div className="text-xs text-gray-500 pt-2 border-t">
              Config source: <Badge variant="outline" className="ml-1 text-xs">
                {health.configSource?.toUpperCase() || 'DEFAULT'}
              </Badge>
            </div>
          </>
        ) : (
          <div className="h-20 flex items-center justify-center">
            {loading ? (
              <ReloadIcon className="h-6 w-6 animate-spin" />
            ) : (
              <span className="text-gray-500">No data available</span>
            )}
          </div>
        )}
      </CardContent>

      <CardFooter className="flex justify-between pt-2 border-t">
        <div className="text-xs text-gray-500">
          {lastUpdated
            ? `Updated ${formatDistanceToNow(lastUpdated)} ago`
            : "Never updated"}
        </div>
        <div className="flex space-x-2">
          <Button
            onClick={toggleAutoRefresh}
            variant="outline"
            size="sm"
          >
            {refreshInterval !== null ? "Stop" : "Auto-refresh"}
          </Button>
          <Button
            onClick={fetchStatus}
            size="sm"
            disabled={loading}
          >
            {loading ? (
              <ReloadIcon className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <ReloadIcon className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}

// SharePoint Rate Limit Configuration Editor
interface SharePointRateLimitConfigEditorProps {
  currentConfig?: SharePointRateLimitConfig;
  onConfigUpdated?: () => void;
}

export function SharePointRateLimitConfigEditor({ currentConfig, onConfigUpdated }: SharePointRateLimitConfigEditorProps) {
  const [requestsPerSecond, setRequestsPerSecond] = useState<number>(currentConfig?.requestsPerSecond || 5);
  const [maxConcurrent, setMaxConcurrent] = useState<number>(currentConfig?.maxConcurrentRequests || 10);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [fetchedConfig, setFetchedConfig] = useState<SharePointRateLimitConfig | null>(null);

  // Fetch config on mount
  useEffect(() => {
    const fetchConfig = async () => {
      const response = await getSharePointRateLimitConfig();
      if (response.success && response.config) {
        setFetchedConfig(response.config);
        setRequestsPerSecond(response.config.requestsPerSecond);
        setMaxConcurrent(response.config.maxConcurrentRequests);
      }
    };
    fetchConfig();
  }, []);

  // Update local state when config prop changes
  useEffect(() => {
    if (currentConfig) {
      setRequestsPerSecond(currentConfig.requestsPerSecond);
      setMaxConcurrent(currentConfig.maxConcurrentRequests);
    }
  }, [currentConfig]);

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    setSaving(true);

    try {
      if (requestsPerSecond < 1 || requestsPerSecond > 100) {
        throw new Error('Requests per second must be between 1 and 100');
      }
      if (maxConcurrent < 1 || maxConcurrent > 100) {
        throw new Error('Max concurrent must be between 1 and 100');
      }
      if (maxConcurrent < requestsPerSecond) {
        throw new Error('Max concurrent must be >= requests per second');
      }

      const response = await updateSharePointRateLimitConfig(requestsPerSecond, maxConcurrent);
      if (response.success) {
        setSuccess(`SharePoint rate limits updated: ${requestsPerSecond}/sec, max ${maxConcurrent}`);
        if (response.config) {
          setFetchedConfig(response.config);
        }
        onConfigUpdated?.();
      } else {
        throw new Error(response.error || 'Failed to update config');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    setError(null);
    setSuccess(null);
    setResetting(true);

    try {
      const response = await resetSharePointRateLimitConfig();
      if (response.success) {
        setSuccess('SharePoint rate limits reset to defaults');
        if (response.config) {
          setRequestsPerSecond(response.config.requestsPerSecond);
          setMaxConcurrent(response.config.maxConcurrentRequests);
          setFetchedConfig(response.config);
        }
        onConfigUpdated?.();
      } else {
        throw new Error(response.error || 'Failed to reset config');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setResetting(false);
    }
  };

  const displayConfig = currentConfig || fetchedConfig;

  return (
    <Card>
      <CardHeader>
        <CardTitle>SharePoint Rate Limit Settings</CardTitle>
        <CardDescription>
          Control rate limiting for Microsoft Graph API (SharePoint) requests.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {error && (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        {success && (
          <Alert>
            <AlertTitle>Success</AlertTitle>
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="spRequestsPerSecond">Requests/Second</Label>
            <Input
              id="spRequestsPerSecond"
              type="number"
              min={1}
              max={100}
              value={requestsPerSecond}
              onChange={(e) => setRequestsPerSecond(parseInt(e.target.value) || 1)}
            />
            <p className="text-xs text-gray-500">
              How fast new requests can start (default: 5)
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="spMaxConcurrent">Max Concurrent Requests</Label>
            <Input
              id="spMaxConcurrent"
              type="number"
              min={1}
              max={100}
              value={maxConcurrent}
              onChange={(e) => setMaxConcurrent(parseInt(e.target.value) || 1)}
            />
            <p className="text-xs text-gray-500">
              Maximum requests running simultaneously (default: 10)
            </p>
          </div>
        </div>

        {/* Quick presets */}
        <div className="pt-2">
          <p className="text-xs text-gray-500 mb-2">Quick presets:</p>
          <div className="flex gap-2 flex-wrap">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setRequestsPerSecond(2); setMaxConcurrent(5); }}
            >
              Low (2/sec, max 5)
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setRequestsPerSecond(5); setMaxConcurrent(10); }}
            >
              Normal (5/sec, max 10)
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setRequestsPerSecond(10); setMaxConcurrent(20); }}
            >
              High (10/sec, max 20)
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setRequestsPerSecond(15); setMaxConcurrent(30); }}
            >
              Maximum (15/sec, max 30)
            </Button>
          </div>
        </div>

        {displayConfig && (
          <div className="text-xs text-gray-500 pt-2 border-t">
            Current config source: <Badge variant="outline" className="ml-1">
              {displayConfig.source.toUpperCase()}
            </Badge>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button
          variant="outline"
          onClick={handleReset}
          disabled={resetting || saving}
        >
          {resetting ? (
            <ReloadIcon className="h-4 w-4 animate-spin mr-2" />
          ) : null}
          Reset to Defaults
        </Button>
        <Button
          onClick={handleSave}
          disabled={saving || resetting}
        >
          {saving ? (
            <ReloadIcon className="h-4 w-4 animate-spin mr-2" />
          ) : null}
          Save Changes
        </Button>
      </CardFooter>
    </Card>
  );
}
